import { createContext, useContext, useMemo, useState, useEffect, useCallback, useRef, ReactNode } from "react"
import { useSessionState } from "@/hooks/useSessionState"
import { useService, useServiceEventListener, useServiceGroup } from "@/hooks/useService"
import { getWorkspace as getCachedWorkspace, putWorkspace as cacheWorkspace } from "@/db/workspaceDatabase"
import { useEventListeners, type EventListeners } from "@/hooks/useEventListeners"
import { useTTSOptional } from "@/tts"
import { useDevice } from "@/components/responsive"
import { useTabs } from "./tabsContext"
import {
  type AccessRequestStatus,
  type Workspace,
  type WorkspaceMember,
  type IWorkspaceLibraryService,
  type QueryMembersQuery,
  type QueryMembersOptions,
  type QueryMembersResultPromise,
  type AddMemberErrorCodes,
  type RemoveMemberErrorCodes,
  type RequestAccessErrorCodes,
  type WorkspaceUpdatedEvent,
  type WorkspaceAccessGrantedEvent,
  type WorkspaceAccessDeniedEvent,
  type MemberAddedEvent,
  type MemberRemovedEvent,
  type WorkspaceDeletedEvent,
  WORKSPACE_LIBRARY_SERVICE_IDENT,
  RPC_EVENT_WORKSPACE_UPDATED_IDENT,
  RPC_EVENT_WORKSPACE_ACCESS_GRANTED_IDENT,
  RPC_EVENT_WORKSPACE_ACCESS_DENIED_IDENT,
  RPC_EVENT_MEMBER_ADDED_IDENT,
  RPC_EVENT_MEMBER_REMOVED_IDENT,
  RPC_EVENT_WORKSPACE_DELETED_IDENT,
} from "@bloomstack/apis/workspace"
import {
  type StreamState,
  type StreamStateChangedEvent,
  type IDataStreamManagerService,
  DATASTREAM_MANAGER_SERVICE_IDENT,
  RPC_EVENT_STREAM_STATE_CHANGED_IDENT,
} from "@bloomstack/apis/services/dataStreamManager"
import type { ResultPromise } from "@bloomstack/greenhouse/results"
import { RPC_TIER_ACTIVE_WORKSPACE, RPC_TIER_INACTIVE_WORKSPACE } from "@/constants/rpcPriorities";
import Dynamic from "@/lib/dynamic";
import ComponentSpinner from "@/components/ui/componentSpinner";

const ChatBubbleLeftRightIcon = Dynamic(
  () =>
    import("@heroicons/react/24/outline").then(
      (mod) => mod.ChatBubbleLeftRightIcon
    ),
  { ssr: false, loading: () => <ComponentSpinner /> }
);

const LockClosedIcon = Dynamic(
  () =>
    import("@heroicons/react/24/outline").then(
      (mod) => mod.LockClosedIcon
    ),
  { ssr: false, loading: () => <ComponentSpinner /> }
);

const ClockIcon = Dynamic(
  () =>
    import("@heroicons/react/24/outline").then(
      (mod) => mod.ClockIcon
    ),
  { ssr: false, loading: () => <ComponentSpinner /> }
);

const XCircleIcon = Dynamic(
  () =>
    import("@heroicons/react/24/outline").then(
      (mod) => mod.XCircleIcon
    ),
  { ssr: false, loading: () => <ComponentSpinner /> }
);

// ============================================================================
// TYPES
// ============================================================================

/**
 * Events emitted by the ChatView context.
 * Children can subscribe to these events without causing re-renders.
 */
export type WorkspaceEvents = {
  workspaceUpdated: (workspace: Workspace) => void
  memberAdded: (member: WorkspaceMember) => void
  memberRemoved: (workspaceId: string, userId: string) => void
}

export type ChatViewContextType = {
  /** The rc-dock tab ID (e.g., "workspace:abc123") */
  tabId: string
  /** The workspace/entity ID (e.g., "abc123") */
  workspaceId: string
  /** The stream ID for chat messages (from workspace.activeStreamId) */
  streamId: string | null
  
  // Workspace data
  /** The workspace object, null while loading or on error */
  workspace: Workspace | null
  /** Whether the workspace is loading */
  isLoading: boolean
  /** Error from loading workspace */
  error: Error | null
  /** Whether the workspace is currently auto-retrying after a load failure */
  isRetrying: boolean
  /** Number of auto-retry attempts so far (for "Retrying... attempt N" UI) */
  retryAttempt: number
  /** Manually trigger a workspace re-fetch (cancels pending auto-retry) */
  retryWorkspace: () => void
  
  // Stream state (for UI feedback)
  /** Current stream state - running status, feedback, active entry */
  streamState: StreamState | null
  
  // Events - children subscribe without re-renders
  /** Event bus for workspace events */
  events: EventListeners<WorkspaceEvents>
  
  // UI state
  /** Whether chat input is hidden (readonly mode) */
  readonlyMode: boolean
  /** Toggle readonly mode */
  setReadonlyMode: (value: boolean) => void
  /** Whether to auto-maximize tab on input focus (mobile: always on, desktop: user toggle) */
  autoMaximize: boolean
  /** Toggle auto-maximize behavior */
  setAutoMaximize: (value: boolean) => void
  /** Whether agent message backgrounds are colorized from avatar colors */
  colorizeMessages: boolean
  /** Toggle message colorization */
  setColorizeMessages: (value: boolean) => void
  
  // Member management methods
  /** Add a member to the workspace */
  addMember: (userId: string) => ResultPromise<WorkspaceMember, AddMemberErrorCodes>
  /** Remove a member from the workspace by their profile ID */
  removeMember: (userProfileId: string) => ResultPromise<boolean, RemoveMemberErrorCodes>
  /** Query members of the workspace */
  queryMembers: (query?: QueryMembersQuery, options?: QueryMembersOptions) => QueryMembersResultPromise
  
  // Access request flow
  /** Whether user was denied access to this workspace */
  accessDenied: boolean
  /** Whether this workspace has been deleted (blocks chat, shows deleted UI) */
  workspaceDeleted: boolean
  /** Status of user's access request: none, pending, or rejected */
  accessRequestStatus: AccessRequestStatus | null
  /** Request access to this workspace */
  requestAccess: () => ResultPromise<boolean, RequestAccessErrorCodes>
}

// ============================================================================
// CONTEXT
// ============================================================================

const ChatViewContext = createContext<ChatViewContextType | null>(null)

// ============================================================================
// PROVIDER
// ============================================================================

type ChatViewProviderProps = {
  tabId: string
  workspaceId: string
  children: ReactNode
}

/**
 * Provider for ChatView context.
 * 
 * Provides tab identity (tabId, workspaceId, streamId) to all children
 * without prop drilling. Fetches workspace data, syncs stream data,
 * and provides event bus for workspace events.
 * 
 * @example
 * <ChatViewProvider tabId="workspace:abc123" workspaceId="abc123">
 *   <ChatStreamView />
 *   <ChatContextMenu />
 *   <ChatInput />
 * </ChatViewProvider>
 */
export function ChatViewProvider({ tabId, workspaceId, children }: ChatViewProviderProps) {
  // ============================================================================
  // TAB CONTEXT (for updating tab title)
  // ============================================================================
  
  const tabs = useTabs(false) // Don't throw if not in TabsProvider (for testing/storybook)
  
  // ============================================================================
  // WORKSPACE SERVICE
  // ============================================================================
  
  const workspaceService = useService<IWorkspaceLibraryService>(WORKSPACE_LIBRARY_SERVICE_IDENT)
    .withPriority("normal", () => tabs?.getActiveTabId() === tabId ? RPC_TIER_ACTIVE_WORKSPACE : RPC_TIER_INACTIVE_WORKSPACE)
  const dataStreamService = useService<IDataStreamManagerService>(DATASTREAM_MANAGER_SERVICE_IDENT)
    .withPriority("normal", () => tabs?.getActiveTabId() === tabId ? RPC_TIER_ACTIVE_WORKSPACE : RPC_TIER_INACTIVE_WORKSPACE)
  
  // ============================================================================
  // WORKSPACE STATE
  // ============================================================================
  
  const [workspace, setWorkspace] = useState<Workspace | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  
  // Access request state
  const [accessDenied, setAccessDenied] = useState(false)
  const [accessRequestStatus, setAccessRequestStatus] = useState<AccessRequestStatus | null>(null)
  // Whether the workspace was deleted while open (or is already deleted on load)
  const [workspaceDeleted, setWorkspaceDeleted] = useState(false)
  
  // Refetch trigger - bump this to force workspace re-fetch
  const [refetchTrigger, setRefetchTrigger] = useState(0)

  // Auto-retry state for initial workspace load failures
  const [isRetrying, setIsRetrying] = useState(false)
  const [retryAttemptDisplay, setRetryAttemptDisplay] = useState(0)
  const [autoRetryTrigger, setAutoRetryTrigger] = useState(0)
  const retryAttemptRef = useRef(0)
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Keep workspaceService in a ref so the fetch effect doesn't re-run on proxy changes
  const workspaceServiceRef = useRef(workspaceService)
  useEffect(() => { workspaceServiceRef.current = workspaceService }, [workspaceService])

  // Track whether workspace data is currently loaded (without adding workspace to fetch effect deps)
  const hasWorkspaceDataRef = useRef(false)
  useEffect(() => { hasWorkspaceDataRef.current = workspace !== null }, [workspace])

  // Track last fetched workspaceId to detect workspace navigation vs re-fetch of same workspace
  const lastFetchedWorkspaceIdRef = useRef<string | null>(null)

  // ============================================================================
  // STREAM STATE (for UI feedback)
  // ============================================================================
  
  const [streamState, setStreamState] = useState<StreamState | null>(null)
  
  // Derived streamId from workspace
  const streamId = workspace?.activeStreamId ?? null

  // Subscribe to stream group for bulk events (e.g., entries bulk removed)
  useServiceGroup("stream", streamId)
  
  // ============================================================================
  // LOCAL EVENT BUS
  // ============================================================================
  
  const events = useEventListeners<WorkspaceEvents>()
  
  // ============================================================================
  // FETCH WORKSPACE ON MOUNT
  // ============================================================================
  
  useEffect(() => {
    let cancelled = false

    // Cancel any pending retry timer at the start of a new effect run
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current)
      retryTimerRef.current = null
    }

    // Detect workspace navigation — reset retry state and treat as initial load
    const isNewWorkspace = lastFetchedWorkspaceIdRef.current !== workspaceId
    if (isNewWorkspace) {
      lastFetchedWorkspaceIdRef.current = workspaceId
      retryAttemptRef.current = 0
      setRetryAttemptDisplay(0)
      setIsRetrying(false)
      hasWorkspaceDataRef.current = false
    }

    // Only show loading spinner on initial load — don't flash if data already exists
    const isInitialLoad = !hasWorkspaceDataRef.current

    const fetchWorkspace = async () => {
      if (isInitialLoad) {
        // Stale-while-revalidate: show cached data immediately if available
        const cached = await getCachedWorkspace(workspaceId)
        if (cached && !cancelled) {
          setWorkspace(cached)
          hasWorkspaceDataRef.current = true
          setIsLoading(false)
          if (cached.title) {
            tabs?.setTabMetadata(tabId, { title: cached.title, icon: <ChatBubbleLeftRightIcon className="w-4 h-4 mr-1" /> })
          }
        } else {
          setIsLoading(true)
        }
      }
      setError(null)
      setAccessDenied(false)
      setAccessRequestStatus(null)
      setWorkspaceDeleted(false)

      try {
        const result = await workspaceServiceRef.current.read(workspaceId)

        if (cancelled) return

        if (!result.success) {
          // Check if access was denied (user is not a member)
          if (result.error?.code === "ACCESS_DENIED") {
            setAccessDenied(true)

            // Fetch access request status (tab metadata is updated by useEffect below)
            const statusResult = await workspaceServiceRef.current.getAccessRequestStatus(workspaceId)
            if (!cancelled && statusResult.success) {
              setAccessRequestStatus(statusResult.result)
            }

            // Access denied is definitive — clear retry state
            setIsRetrying(false)
            retryAttemptRef.current = 0
            setRetryAttemptDisplay(0)

            // Don't set error for access denied - we handle it specially
            if (isInitialLoad) setIsLoading(false)
            return
          }

          // Workspace not found — it was deleted. Late joiners land here (the
          // RPC deletion event only reaches tabs open at delete time). Treat
          // it as a deletion, not a generic load error.
          if (result.error?.code === "NOT_FOUND") {
            setWorkspaceDeleted(true)
            if (isInitialLoad) setIsLoading(false)
            return
          }

          throw new Error(result.error?.reason ?? 'Failed to load workspace')
        }

        // SUCCESS — clear retry state and update workspace
        hasWorkspaceDataRef.current = true
        retryAttemptRef.current = 0
        setRetryAttemptDisplay(0)
        setIsRetrying(false)
        setWorkspace(result.result)
        cacheWorkspace(result.result)
        if (isInitialLoad) setIsLoading(false)

        // Update tab title with workspace title
        if (result.result.title) {
          tabs?.setTabMetadata(tabId, { title: result.result.title, icon: <ChatBubbleLeftRightIcon className="w-4 h-4 mr-1" /> })
        }
      } catch (err) {
        if (cancelled) return
        console.error('[ChatViewProvider] Failed to load workspace:', err)

        if (isInitialLoad) {
          // Initial load failure: show error and schedule auto-retry with exponential backoff
          setError(err instanceof Error ? err : new Error(String(err)))
          setIsLoading(false)

          retryAttemptRef.current += 1
          setRetryAttemptDisplay(retryAttemptRef.current)
          setIsRetrying(true)

          const delay = Math.min(1000 * Math.pow(2, retryAttemptRef.current - 1), 5000)
          retryTimerRef.current = setTimeout(() => {
            setAutoRetryTrigger(c => c + 1)
          }, delay)
        } else {
          // Background re-fetch failure: keep existing data visible, just log
          console.warn('[ChatViewProvider] Background re-fetch failed, keeping existing data')
        }
      }
    }

    fetchWorkspace()

    return () => {
      cancelled = true
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current)
        retryTimerRef.current = null
      }
    }
  }, [workspaceId, refetchTrigger, autoRetryTrigger])
  
  // ============================================================================
  // ACCESS DENIED TAB METADATA
  // ============================================================================
  
  // Update tab title/icon based on access request status
  useEffect(() => {
    if (!accessDenied) return
    
    switch (accessRequestStatus) {
      case "none":
        tabs?.setTabMetadata(tabId, { 
          title: "Request Access", 
          icon: <LockClosedIcon className="w-4 h-4" /> 
        })
        break
      case "pending":
        tabs?.setTabMetadata(tabId, { 
          title: "Request Pending", 
          icon: <ClockIcon className="w-4 h-4" /> 
        })
        break
      case "rejected":
        tabs?.setTabMetadata(tabId, { 
          title: "Access Denied", 
          icon: <XCircleIcon className="w-4 h-4" /> 
        })
        break
      default:
        // Loading state - show generic
        tabs?.setTabMetadata(tabId, { 
          title: "Requesting Access", 
          icon: <LockClosedIcon className="w-4 h-4" /> 
        })
    }
  }, [accessDenied, accessRequestStatus, tabId, tabs])
  
  // ============================================================================
  // WORKSPACE DELETED TAB METADATA
  // ============================================================================
  
  // Update tab title/icon when the workspace is deleted
  useEffect(() => {
    if (!workspaceDeleted) return
    tabs?.setTabMetadata(tabId, { 
      title: "Workspace Deleted", 
      icon: <XCircleIcon className="w-4 h-4" /> 
    })
  }, [workspaceDeleted, tabId, tabs])
  
  // ============================================================================
  // RPC EVENT LISTENERS → LOCAL EVENT BUS
  // ============================================================================
  
  // Workspace updated event
  useServiceEventListener<WorkspaceUpdatedEvent>(
    RPC_EVENT_WORKSPACE_UPDATED_IDENT,
    (updatedWorkspace) => {
      // Filter by workspaceId
      if (updatedWorkspace.id !== workspaceId) return
      
      // Update local state
      setWorkspace(updatedWorkspace)
      
      // Emit to local event bus
      events.emit.workspaceUpdated(updatedWorkspace)
    },
    [workspaceId, events]
  )
  
  // Member added event
  useServiceEventListener<MemberAddedEvent>(
    RPC_EVENT_MEMBER_ADDED_IDENT,
    (member) => {
      // Filter by workspaceId
      if (member.workspaceId !== workspaceId) return
      
      // Emit to local event bus
      events.emit.memberAdded(member)
    },
    [workspaceId, events]
  )
  
  // Member removed event
  useServiceEventListener<MemberRemovedEvent>(
    RPC_EVENT_MEMBER_REMOVED_IDENT,
    (wsId, userId) => {
      // Filter by workspaceId
      if (wsId !== workspaceId) return
      
      // Emit to local event bus
      events.emit.memberRemoved(wsId, userId)
    },
    [workspaceId, events]
  )
  
  // Access granted event - triggered when owner accepts our access request
  useServiceEventListener<WorkspaceAccessGrantedEvent>(
    RPC_EVENT_WORKSPACE_ACCESS_GRANTED_IDENT,
    (grantedWorkspaceId) => {
      // Filter by workspaceId
      if (grantedWorkspaceId !== workspaceId) return
      
      // Access was granted! Trigger full workspace re-fetch
      // This bumps the dependency that triggers the main fetch useEffect,
      // which will properly reset all state and initialize the chat view
      setRefetchTrigger(prev => prev + 1)
    },
    [workspaceId]
  )
  
  // Access denied event - triggered when owner rejects our access request
  useServiceEventListener<WorkspaceAccessDeniedEvent>(
    RPC_EVENT_WORKSPACE_ACCESS_DENIED_IDENT,
    (deniedWorkspaceId) => {
      // Filter by workspaceId
      if (deniedWorkspaceId !== workspaceId) return
      
      // Access was denied - update status to show rejection UI
      setAccessRequestStatus("rejected")
    },
    [workspaceId]
  )
  
  // Workspace deleted event - triggered when the workspace is deleted
  // while this tab is open. Mirrors the access-denied listener exactly.
  useServiceEventListener<WorkspaceDeletedEvent>(
    RPC_EVENT_WORKSPACE_DELETED_IDENT,
    (deletedWorkspaceId) => {
      // Filter by workspaceId
      if (deletedWorkspaceId !== workspaceId) return
      setWorkspaceDeleted(true)
    },
    [workspaceId]
  )
  
  // ============================================================================
  // STREAM STATE FETCH & SUBSCRIPTION
  // ============================================================================
  
  // Fetch initial stream state when streamId changes
  useEffect(() => {
    if (!streamId) {
      setStreamState(null)
      return
    }
    
    let cancelled = false
    
    const fetchStreamState = async () => {
      try {
        const result = await dataStreamService.getStreamState(streamId)
        if (cancelled) return
        
        if (result.success) {
          setStreamState(result.result)
        } else {
          // Not found or error - set to idle
          setStreamState({ isRunning: false })
        }
      } catch (err) {
        if (cancelled) return
        console.error('[ChatViewProvider] Failed to fetch stream state:', err)
        setStreamState({ isRunning: false })
      }
    }
    
    fetchStreamState()
    
    return () => {
      cancelled = true
    }
  }, [streamId, dataStreamService])
  
  // Subscribe to stream state changes
  useServiceEventListener<StreamStateChangedEvent>(
    RPC_EVENT_STREAM_STATE_CHANGED_IDENT,
    (eventStreamId, state) => {
      // Filter by streamId
      if (eventStreamId !== streamId) return
      
      setStreamState(state)
    },
    [streamId]
  )
  
  // ============================================================================
  // MEMBER MANAGEMENT METHODS
  // ============================================================================
  
  const addMember = useCallback(
    (userId: string) => workspaceService.addMember(workspaceId, userId),
    [workspaceId, workspaceService]
  )
  
  const removeMember = useCallback(
    (userProfileId: string) => workspaceService.removeMember(workspaceId, userProfileId),
    [workspaceId, workspaceService]
  )
  
  const queryMembers = useCallback(
    (query?: QueryMembersQuery, options?: QueryMembersOptions) => 
      workspaceService.queryMembers(workspaceId, query, options),
    [workspaceId, workspaceService]
  )
  
  // ============================================================================
  // ACCESS REQUEST METHOD
  // ============================================================================
  
  const requestAccess = useCallback(async () => {
    const result = await workspaceService.requestAccess(workspaceId)
    if (result.success) {
      setAccessRequestStatus("pending")
    }
    return result
  }, [workspaceId, workspaceService])

  // Manual retry — cancels any pending auto-retry, resets retry counters,
  // and forces a new fetch to show the loading spinner
  const retryWorkspace = useCallback(() => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current)
      retryTimerRef.current = null
    }
    hasWorkspaceDataRef.current = false
    retryAttemptRef.current = 0
    setRetryAttemptDisplay(0)
    setIsRetrying(false)
    setRefetchTrigger(prev => prev + 1)
  }, [])

  // ============================================================================
  // UI STATE
  // ============================================================================
  
  const { isMobile } = useDevice()
  
  // Readonly mode - hides chat input for distraction-free reading
  const [readonlyMode, setReadonlyMode] = useSessionState<boolean>(
    `${workspaceId}:readonly-mode`,
    false
  )
  
  // Auto-maximize on focus - defaults to ON for mobile, OFF for desktop
  const [autoMaximize, setAutoMaximize] = useSessionState<boolean>(
    `${workspaceId}:auto-maximize`,
    isMobile
  )

  // Colorize agent message backgrounds from avatar dominant color
  const [colorizeMessages, setColorizeMessages] = useSessionState<boolean>(
    `${workspaceId}:colorize-messages`,
    true
  )

  // ============================================================================
  // AUTO-SPEAK OBSERVER
  // ============================================================================

  const tts = useTTSOptional();
  // Tracks the entryId we've already auto-spoken, so we start each streaming
  // entry exactly once. The stream's entryId / userProfile / autoSpeak flag can
  // each populate on different renders after `isRunning` flips true, so we key
  // off "running + fully-populated + not-yet-started" rather than the rising
  // edge of isRunning (which can arrive before entryId is known).
  const autoSpokenEntryRef = useRef<string | null>(null);

  useEffect(() => {
    const running = !!streamState?.isRunning;
    const entryId = streamState?.entryId;
    const userProfileId = streamState?.userProfile?.id;

    if (!running) {
      // Stream ended: clear so the next stream can start fresh.
      autoSpokenEntryRef.current = null;
      return;
    }
    if (
      entryId &&
      userProfileId &&
      workspaceId &&
      tts?.isAutoSpeak(workspaceId) &&
      autoSpokenEntryRef.current !== entryId
    ) {
      autoSpokenEntryRef.current = entryId;
      tts.speak([{ entryId, workspaceId, userProfileId }], { streaming: true });
    }
  }, [streamState?.isRunning, streamState?.entryId, streamState?.userProfile?.id, tts, workspaceId]);

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================

  const value = useMemo<ChatViewContextType>(() => ({
    // Identity
    tabId,
    workspaceId,
    streamId,
    
    // Workspace data
    workspace,
    isLoading,
    error,
    isRetrying,
    retryAttempt: retryAttemptDisplay,
    retryWorkspace,

    // Stream state
    streamState,
    
    // Events
    events,
    
    // UI state
    readonlyMode,
    setReadonlyMode,
    autoMaximize,
    setAutoMaximize,
    colorizeMessages,
    setColorizeMessages,
    
    // Member methods
    addMember,
    removeMember,
    queryMembers,
    
    // Access request flow
    accessDenied,
    workspaceDeleted,
    accessRequestStatus,
    requestAccess,
  }), [
    tabId,
    workspaceId,
    streamId,
    workspace,
    isLoading,
    error,
    isRetrying,
    retryAttemptDisplay,
    retryWorkspace,
    streamState,
    events,
    readonlyMode,
    setReadonlyMode,
    autoMaximize,
    setAutoMaximize,
    colorizeMessages,
    setColorizeMessages,
    addMember,
    removeMember,
    queryMembers,
    accessDenied,
    workspaceDeleted,
    accessRequestStatus,
    requestAccess,
  ])

  return (
    <ChatViewContext.Provider value={value}>
      {children}
    </ChatViewContext.Provider>
  )
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook to access ChatView context (tabId, workspaceId, streamId).
 * 
 * @example
 * const { tabId, workspaceId } = useChatView()
 * 
 * // For components that may exist outside ChatViewProvider:
 * const chatView = useChatView(false) // returns undefined if not in provider
 */
export function useChatView(throwIfMissing?: true): ChatViewContextType
export function useChatView(throwIfMissing: false): ChatViewContextType | undefined
export function useChatView(throwIfMissing = true): ChatViewContextType | undefined {
  const ctx = useContext(ChatViewContext)
  if (!ctx && throwIfMissing) {
    throw new Error("useChatView must be used within a ChatViewProvider")
  }
  return ctx ?? undefined
}
